import { useState } from "react";
import tw from "tailwind-styled-components";
import { MdClose } from "react-icons/md";
import { ScrollableContent } from "../../Styles/InputStyles";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaRegCalendar } from "react-icons/fa";
import moment from "moment";
import {
    InputGroup,
    FieldWrappers,
    Label,
    SubmitBtn,
    SideCancelBtn,
    Wrapper,
    ActionSection,
    Note,
    SelectionCard,
    CardWrapper,
} from "../../Styles/InputStyles";

const MoveInManually = ({ setIsMoveIn, selectedData, moveInLoading, moveInMutate }) => {
    const [openCalendar, setOpenCalendar] = useState(null);
    const onClose = () => setIsMoveIn(false);

    const rentalCycleOptions = [
        { label: "1st to 1st", value: "01" },
        { label: "15th to 15th", value: "15" },
        { label: "Move-In Date", value: "00" },
    ];

    const getInitialRentalCycle = () => {
        const currentCycle = selectedData?.rentalCycle;
        if (currentCycle && currentCycle !== "01" && currentCycle !== "15") {
            return "00";
        }
        return currentCycle || "01";
    };

    const initialValues = {
        moveInDate: selectedData?.moveInDate || "",
        rentalCycle: getInitialRentalCycle(),
    };

    const validationSchema = Yup.object().shape({
        moveInDate: Yup.date()
            .required("Move-in date is required"),
        rentalCycle: Yup.string()
            .required("Rental cycle is required")
            .oneOf(["01", "15", "00"], "Invalid rental cycle"),
    });

    const handleSubmit = (values) => {
        const payload = {
            tenantId: selectedData?.tenantId,
            moveInDate: moment(values.moveInDate).format("YYYY-MM-DD"),
            rentalCycle: values.rentalCycle,
        };

        moveInMutate(payload);
    };

    return (
        <Container>
            {/* Header */}
            <Header>
                <Title>Move-In</Title>
                <button onClick={onClose}>
                    <MdClose color="white" size={24} />
                </button>
            </Header>

            {/* Top Section */}
            <ProfileSection>
                <UserInfo>
                    <span className="font-medium mb-0 text-lg">
                        {selectedData?.name}
                    </span>
                    <span className="text-sm text-gray-600">
                        #{selectedData?.roomNum}, {selectedData?.propertyName}
                    </span>
                </UserInfo>
                <div className="flex flex-col gap-0">
                    <span className='text-gray-500 text-sm'>{'Rent'}</span>
                    <span className='text-xl font-semibold text-gray-700'>
                        {'₹' + selectedData?.rent}
                    </span>
                </div>
            </ProfileSection>

            {/* Content */}
            <Content>
                <ScrollableContent>
                    <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                        enableReinitialize
                    >
                        {({ values, setFieldValue, errors, touched, isSubmitting }) => {
                            const handleMoveInDateChange = (date) => {
                                const formattedDate = moment(date).format("YYYY-MM-DD");
                                setFieldValue("moveInDate", formattedDate);
                                setOpenCalendar(null);
                                if (values.rentalCycle !== "01" && values.rentalCycle !== "15") {
                                    setFieldValue("rentalCycle", "00");
                                }
                            };

                            return (
                                <Form>
                                    <Wrapper className='!mt-0'>
                                        <InputGroup>
                                            <Label htmlFor="moveInDate">Move-in Date *</Label>
                                            <FieldWrappers className="bg-gray-100">
                                                <DatePicker
                                                    selected={values.moveInDate ? new Date(values.moveInDate) : null}
                                                    onChange={handleMoveInDateChange}
                                                    onClickOutside={() => setOpenCalendar(null)}
                                                    onSelect={() => setOpenCalendar(null)}
                                                    open={openCalendar === 'moveIn'}
                                                    onInputClick={() => {
                                                        setOpenCalendar(openCalendar === 'moveIn' ? null : 'moveIn');
                                                    }}
                                                    dateFormat="yyyy-MM-dd"
                                                    className="cursor-not-allowed w-full"
                                                    placeholderText="Select new move-in date"
                                                    showIcon
                                                    icon={<FaRegCalendar className="text-gray-400 text-xs mt-0.5" />}
                                                    showPopperArrow={false}
                                                    onFocus={(e) => e.target.blur()}
                                                    wrapperClassName="w-full"
                                                    name="moveInDate"
                                                    id="moveInDate"
                                                    autoComplete="off"
                                                    portalId="root-portal"
                                                    popperClassName="react-datepicker-popper"
                                                    disabled
                                                />
                                            </FieldWrappers>
                                            {touched.moveInDate && errors.moveInDate && (
                                                <p className="text-rose-600 text-sm mt-1">{errors.moveInDate}</p>
                                            )}
                                        </InputGroup>

                                        {/* Rent Cycle */}
                                        <InputGroup>
                                            <Label>Rental Cycle *</Label>
                                            <CardWrapper>
                                                {rentalCycleOptions.map((mode) => (
                                                    <SelectionCard
                                                        key={mode.value}
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            if (mode.value === "00" && !values.moveInDate) {
                                                                alert("Please select a move-in date first when choosing 'Move-In Date' cycle");
                                                                return;
                                                            }
                                                            setFieldValue("rentalCycle", mode.value);
                                                        }}
                                                        className={
                                                            values.rentalCycle === mode.value
                                                                ? "bg-[#26A69A] text-white px-4 lg:px-6 cursor-not-allowed"
                                                                : "bg-gray-100 text-gray-600 px-4 lg:px-6 cursor-not-allowed"
                                                        }
                                                        disabled
                                                    >
                                                        <span className="text-sm">{mode.label}</span>
                                                    </SelectionCard>
                                                ))}
                                            </CardWrapper>
                                            {touched.rentalCycle && errors.rentalCycle && (
                                                <p className="text-rose-600 text-sm mt-1">{errors.rentalCycle}</p>
                                            )}

                                        </InputGroup>

                                        <Note>
                                            <FaRegCalendar className="mt-0.5 mr-1" />
                                            {values.rentalCycle === "01"
                                                ? "Rent due will be generated on the 1st of each month."
                                                : values.rentalCycle === "15"
                                                    ? "Rent due will be generated on the 15th of each month."
                                                    : "Rent due will be generated each month based on move-in date."}
                                        </Note>
                                    </Wrapper>

                                    {/* Action Buttons */}
                                    <ActionSection>
                                        <SideCancelBtn
                                            type="button"
                                            onClick={onClose}
                                        >
                                            Cancel
                                        </SideCancelBtn>
                                        <SubmitBtn
                                            type="submit"
                                            disabled={moveInLoading}
                                        >
                                            {moveInLoading ? "Processing..." : "Move-in"}
                                        </SubmitBtn>
                                    </ActionSection>
                                </Form>
                            );
                        }}
                    </Formik>
                </ScrollableContent>
            </Content>
        </Container>
    );
};

export default MoveInManually;

// ---------------- Styled Components ----------------
const Header = tw.div`flex items-center justify-between bg-[#26A69A] py-4 px-4`;
const Container = tw.div`bg-white w-full h-full min-h-[100vh] flex flex-col`;
const Content = tw.div`p-4 flex flex-col`;
const Title = tw.h1`text-lg md:text-xl font-medium text-center text-white`;
const UserInfo = tw.div`flex flex-col`;
const ProfileSection = tw.div`flex items-center justify-between bg-gray-100 px-4 p-4`;